import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { generateInviteToken, hashToken } from '@/lib/inviteToken';

const DEFAULT_EXPIRY_DAYS = 7;

export function InviteCandidatePage() {
  const { id } = useParams<{ id: string }>();
  const { appUser, canAccessCompany, companyCountry } = useAuth();
  const [processTitle, setProcessTitle] = useState('');
  const [processStatus, setProcessStatus] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState<string | undefined>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !appUser) return;
    getDoc(doc(db, 'processes', id)).then((snap) => {
      if (snap.exists() && canAccessCompany(snap.data().companyId)) {
        setProcessTitle(snap.data().title ?? '');
        setProcessStatus(snap.data().status ?? null);
      }
    });
  }, [id, appUser, canAccessCompany]);

  const createInviteAndGetLink = async (): Promise<string> => {
    if (!id || !appUser?.companyId) throw new Error('Faltan datos');
    const token = generateInviteToken();
    const tokenHash = await hashToken(token);
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + DEFAULT_EXPIRY_DAYS);

    const candidateRef = await addDoc(collection(db, 'candidates'), {
      companyId: appUser.companyId,
      processId: id,
      fullName: fullName || null,
      email: email || null,
      phone: phone?.trim() || null,
      status: 'invited',
      createdAt: new Date().toISOString(),
    });

    const inviteRef = await addDoc(collection(db, 'invites'), {
      companyId: appUser.companyId,
      processId: id,
      candidateId: candidateRef.id,
      tokenHash,
      tokenExpiresAt: expiresAt.toISOString(),
      createdAt: new Date().toISOString(),
    });

    await setDoc(doc(db, 'inviteTokenLookup', tokenHash), {
      inviteId: inviteRef.id,
      candidateId: candidateRef.id,
      processId: id,
      companyId: appUser.companyId,
      tokenExpiresAt: expiresAt.toISOString(),
    });

    const baseUrl = window.location.origin;
    return `${baseUrl}/c/${token}`;
  };

  const handleSaveAndCopy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !appUser?.companyId) return;
    if (processStatus === 'closed') {
      setError('Este proceso está finalizado. No se pueden enviar nuevas invitaciones.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const link = await createInviteAndGetLink();
      setInviteLink(link);
      await navigator.clipboard.writeText(link);
      window.alert('Enlace copiado al portapapeles.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al crear la invitación');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !appUser?.companyId) return;
    if (processStatus === 'closed') {
      setError('Este proceso está finalizado. No se pueden enviar nuevas invitaciones.');
      return;
    }
    const emailTrim = email?.trim();
    if (!emailTrim) {
      setError('El correo es obligatorio para enviar por email.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const link = await createInviteAndGetLink();
      setInviteLink(link);
      const sendEmail = httpsCallable(getFunctions(), 'sendAssessmentEmail');
      await sendEmail({
        toEmail: emailTrim,
        employeeName: fullName?.trim() || 'Candidato',
        assessmentUrl: link,
        testType: 'Evaluación de Selección',
      });
      window.alert('Correo enviado con éxito.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al enviar el correo');
    } finally {
      setLoading(false);
    }
  };

  if (!id) return null;

  if (processStatus === 'closed') {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/app/processes/${id}/candidates`}>← Candidatos</Link>
        </Button>
        <Card className="max-w-xl">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              Este proceso está finalizado. No se pueden enviar nuevas invitaciones.
            </p>
            <Button variant="outline" className="mt-4" asChild>
              <Link to={`/app/processes/${id}`}>Volver al proceso</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to={`/app/processes/${id}/candidates`}>← Candidatos</Link>
      </Button>
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Invitar candidato</CardTitle>
          <p className="text-sm text-muted-foreground">
            Proceso: {processTitle || id}. El enlace caduca en {DEFAULT_EXPIRY_DAYS} días.
          </p>
        </CardHeader>
        <CardContent>
          {inviteLink ? (
            <div className="space-y-2">
              <p className="text-sm font-medium">Enlace de invitación (cópialo y compártelo una sola vez):</p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={inviteLink}
                  className="flex h-10 flex-1 rounded-md border border-input bg-muted px-3 py-2 text-sm"
                />
                <Button
                  variant="outline"
                  onClick={() => navigator.clipboard.writeText(inviteLink)}
                >
                  Copiar
                </Button>
              </div>
              <Button
                variant="secondary"
                onClick={() => {
                  setInviteLink(null);
                  setFullName('');
                  setEmail('');
                  setPhone('');
                }}
              >
                Invitar otro
              </Button>
            </div>
          ) : (
            <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
              {error && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
              )}
              <div className="space-y-2">
                <Label htmlFor="fullName">Nombre (opcional)</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Nombre del candidato"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email (opcional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="candidato@ejemplo.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono (opcional)</Label>
                <div
                  className="[&_.PhoneInput]:flex [&_.PhoneInput]:gap-2 [&_.PhoneInputInput]:flex [&_.PhoneInputInput]:h-10 [&_.PhoneInputInput]:flex-1 [&_.PhoneInputInput]:rounded-md [&_.PhoneInputInput]:border [&_.PhoneInputInput]:border-input [&_.PhoneInputInput]:bg-background [&_.PhoneInputInput]:px-3 [&_.PhoneInputInput]:py-2 [&_.PhoneInputInput]:text-sm [&_.PhoneInputCountryIcon]:!h-5 [&_.PhoneInputCountryIcon]:!w-[1.875rem]"
                >
                  <PhoneInput
                    defaultCountry={(companyCountry === 'OTHER' ? 'US' : companyCountry) || 'MX'}
                    value={phone}
                    onChange={(val) => setPhone(val ?? '')}
                    placeholder="+52 55 1234 5678"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={loading}
                  onClick={handleSaveAndCopy}
                >
                  {loading ? 'Guardando...' : 'Guardar y Copiar Enlace'}
                </Button>
                <Button type="button" disabled={loading} onClick={handleSaveAndSendEmail}>
                  {loading ? 'Guardando...' : 'Guardar y Enviar por Correo'}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
